/**
 * Minimal focus-trap helper for dialog primitives.
 *
 * Usage: from a `keydown` handler on `Tab`, call `trapFocus(event, container)`.
 * If focus is on the first/last focusable inside `container`, wraps it.
 *
 * No external dependency by design — keeps Modal/BottomSheet self-contained.
 */
const FOCUSABLE_SELECTOR =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), iframe, object, embed, [contenteditable="true"]';

export function getFocusable(container: HTMLElement): HTMLElement[] {
  const list = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  return Array.from(list).filter(
    (el) => !el.hasAttribute('hidden') && el.offsetParent !== null,
  );
}

export function trapFocus(e: KeyboardEvent, container: HTMLElement): void {
  const focusable = getFocusable(container);
  if (focusable.length === 0) {
    e.preventDefault();
    container.focus();
    return;
  }
  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;
  const active = document.activeElement as HTMLElement | null;

  if (e.shiftKey) {
    if (active === first || active === container || !container.contains(active)) {
      e.preventDefault();
      last.focus();
    }
  } else {
    if (active === last) {
      e.preventDefault();
      first.focus();
    }
  }
}
